import { useEffect, useState } from "react";

/** Minimal history-API router: the Worker serves index.html for every non-/api path. */
export type RouteState = { recipientIds?: number[]; reply?: ReplyTarget };

/**
 * A reply to a request with a reply envelope (PLAN.md 4.21), from Correspondence to Write: the wrap
 * being answered, its subject, the time the reply must open (unix seconds; null: my choice), the key
 * that signed the request, and how many replies I sent already.
 */
export type ReplyTarget = { id: string; subject: string | null; unlock: number | null; authorKey: string; count: number };

export function navigate(path: string, state: RouteState | null = null): void {
  if (path === location.pathname + location.search && JSON.stringify(history.state) === JSON.stringify(state)) return;
  history.pushState(state, "", path);
  dispatchEvent(new PopStateEvent("popstate"));
}

/** Recipient ids travel in local browser history, never in a URL or a public group label. */
export function useRecipientIds(): number[] {
  const read = () => {
    const ids: unknown = history.state?.recipientIds;
    return Array.isArray(ids) && ids.length <= 20 && ids.every((id) => Number.isSafeInteger(id) && id > 0) ? [...new Set<number>(ids)] : [];
  };
  const [ids, setIds] = useState(read);
  useEffect(() => {
    const change = () => setIds(read());
    addEventListener("popstate", change);
    return () => removeEventListener("popstate", change);
  }, []);
  return ids;
}

const HEX64 = /^[0-9a-f]{64}$/;

/** The reply target of the current history entry, if Write was opened to answer an envelope. */
export function useReplyTarget(): ReplyTarget | null {
  const read = (): ReplyTarget | null => {
    const r: unknown = history.state?.reply;
    if (typeof r !== "object" || r === null) return null;
    const x = r as Record<string, unknown>;
    const ok = typeof x.id === "string" && HEX64.test(x.id) && typeof x.authorKey === "string" && HEX64.test(x.authorKey) && (x.unlock === null || Number.isSafeInteger(x.unlock)) && (x.subject === null || typeof x.subject === "string") && Number.isSafeInteger(x.count);
    return ok ? { id: x.id as string, subject: x.subject as string | null, unlock: x.unlock as number | null, authorKey: x.authorKey as string, count: x.count as number } : null;
  };
  const [target, setTarget] = useState(read);
  useEffect(() => {
    const change = () => setTarget(read());
    addEventListener("popstate", change);
    return () => removeEventListener("popstate", change);
  }, []);
  return target;
}

export function usePath(): string {
  const [path, setPath] = useState(location.pathname);
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    addEventListener("popstate", onPop);
    return () => removeEventListener("popstate", onPop);
  }, []);
  return path;
}

export function queryParam(name: string): string | null {
  return new URLSearchParams(location.search).get(name);
}

export function useQueryParam(name: string): string | null {
  const [value, setValue] = useState(() => queryParam(name));
  useEffect(() => {
    const change = () => setValue(queryParam(name));
    addEventListener("popstate", change);
    return () => removeEventListener("popstate", change);
  }, [name]);
  return value;
}
